import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Typography } from "@mui/material";
import { keyframes } from "@emotion/react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CodingProgressIndicator from "./CodingProgressIndicator";
import XSifterCodableItem, { NOT_RELEVANT_LABEL, RELEVANT_LABEL } from "./XSifterCodableItem";
import XSifterCodableItemNotes from "./XSifterCodableItemNotes";
import { IXSifterUser } from "../../types/XSifterAppUser";
import { ISampledPostItem, ISampledPostItemCollection } from "../../types/XSifterQueries";
import { useAppDispatch } from "../../store/hooks";
import { showApiError } from "../../services/ApiErrorHelper";

const CODING_COMPLETE_MESSAGES = [
    { emoji: "🎉", text: "Congratulations! You've labeled every item in the sample." },
    { emoji: "⭐", text: "You are a superstar! All items have been labeled." },
    { emoji: "🏆", text: "Nice work! You've coded the entire sample." },
    { emoji: "🙌", text: "All done, and done well! Every item has been labeled." },
    { emoji: "🚀", text: "You crushed it! The full sample has been labeled." },
    { emoji: "🎉", text: "Woohoo! Every single item has been labeled." },
];

const CONFETTI_COLORS = ["#f94144", "#f3722c", "#f9c74f", "#90be6d", "#43aa8b", "#577590", "#9b5de5"];
const CONFETTI_PIECE_COUNT = 40;

const confettiFall = keyframes`
    0% { transform: translateY(-10px) rotate(0deg); opacity: 1; }
    100% { transform: translateY(400px) rotate(720deg); opacity: 0; }
`;

interface IConfettiPiece {
    id: number;
    left: number;
    color: string;
    size: number;
    delay: number;
    duration: number;
    round: boolean;
}

const createConfettiPieces = (): IConfettiPiece[] =>
    Array.from({ length: CONFETTI_PIECE_COUNT }, (_, id) => ({
        id,
        left: Math.random() * 100,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        size: 5 + Math.random() * 6,
        delay: Math.random() * 0.5,
        duration: 4.5 + Math.random() * 1,
        round: Math.random() > 0.5,
    }));

interface IXSifterRevisionWorkflowProps {
    codingRoundId: string;
    roundNumber: number;
    savedQueryId: string | null;
    currentUser: IXSifterUser | undefined;
    sampleItemCollection: ISampledPostItemCollection | null,
    onForceHeaderRemount?: () => void;
    onScoreCurrentItem: (item: ISampledPostItem, label: string) => Promise<boolean>,
    onCancelWorkflow: () => void;
    onSubmitForRefinement: () => void;
    onUpdateSampleItems: (updater: (items: ISampledPostItem[]) => ISampledPostItem[]) => void;
}

const XSifterRevisionWorkflow: React.FC<IXSifterRevisionWorkflowProps> = (props: IXSifterRevisionWorkflowProps) => {
    const dispatch = useAppDispatch();
    const [currentItemIndex, setCurrentItemIndex] = useState<number>(-1);
    const [forceReloadTick, setForceReload] = useState<number>(0);
    const [showIncompleteCodingWarning, setShowIncompleteCodingWarning] = useState<boolean>(false);
    const currentItemRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // set the index to the first codable item, or, -1 if none
        setCurrentItemIndex(-1);
        if (props.sampleItemCollection?.items) {
            for (let i=0; i < props.sampleItemCollection?.items?.length || 0; i++) {
                if (props.sampleItemCollection.items[i].label) {
                    continue;
                }
                setCurrentItemIndex(i);
                break;
            }
        }
    }, []);

    const forceReload = () => {
        setForceReload(v => v + 1);
    }

    // when an item is recoded, it goes from its plain preview back to its full
    // rendered tweet embed; re-run the Twitter widget script so that embed gets
    // rendered instead of showing as raw, unstyled blockquote HTML
    useEffect(() => {
        if (forceReloadTick === 0) {
            return;
        }
        const twttr = (window as any).twttr;
        if (twttr && typeof twttr.widgets?.load === "function") {
            twttr.widgets.load();
        }
    }, [forceReloadTick]);

    const updateItemLabel = (itemId: string, label: string | null) => {
        props.onUpdateSampleItems((items) =>
            items.map((item) => item.itemId === itemId ? { ...item, label } : item)
        );
    }

    const items = props.sampleItemCollection?.items ?? [];

    const totalSampleItems= items.length;
    const boundedItemIndex = items.length === 0
        ? -1
        : Math.min(Math.max(currentItemIndex, -1), items.length - 1);
    const currentItem = boundedItemIndex >= 0 ? items[boundedItemIndex] : null;
    const itemsScored = items.filter((item) => !!item.label).length;
    const isSamplingComplete = totalSampleItems > 0 && itemsScored >= totalSampleItems;
    const codingCompleteMessage = useMemo(
        () => CODING_COMPLETE_MESSAGES[Math.floor(Math.random() * CODING_COMPLETE_MESSAGES.length)],
        [isSamplingComplete]
    );
    const hasPlayedConfettiRef = useRef<boolean>(false);
    useEffect(() => {
        hasPlayedConfettiRef.current = false;
    }, [props.codingRoundId]);
    const confettiPieces = useMemo(() => {
        if (!isSamplingComplete || hasPlayedConfettiRef.current) {
            return [];
        }
        hasPlayedConfettiRef.current = true;
        return createConfettiPieces();
    }, [isSamplingComplete, props.codingRoundId]);
    const hasPreviousItem = boundedItemIndex > 0;
    const hasNextItem = boundedItemIndex >= 0 && boundedItemIndex < items.length - 1;

    const scoreCurrentItem = (label: string) => {
        if (!currentItem) {
            return;
        }

        props.onScoreCurrentItem(currentItem, label)
            .then((response) => {
                updateItemLabel(currentItem.itemId, label);
                if (response === true) {
                    setCurrentItemIndex(index => index + 1);
                }
            }).catch((error) => {
                showApiError(dispatch, error, "Could not save your score");
            });
    };

    const onMarkRelevant = useCallback(() => {
        scoreCurrentItem(RELEVANT_LABEL);
    }, [scoreCurrentItem])

    const onMarkNotRelevant = useCallback(() => {
        scoreCurrentItem(NOT_RELEVANT_LABEL);
    }, [scoreCurrentItem])

    const onRecodeCurrentItem = () => {
        if (!currentItem) {
            return;
        }
        updateItemLabel(currentItem.itemId, null);
        forceReload();
    }

    const onRefineQueryClicked = () => {
        if (itemsScored < totalSampleItems) {
            setShowIncompleteCodingWarning(true);
            return;
        }
        props.onSubmitForRefinement();
    }

    const onCancelIncompleteCodingWarning = () => {
        setShowIncompleteCodingWarning(false);
        forceReload();
    }

    const onConfirmIncompleteCodingWarning = () => {
        setShowIncompleteCodingWarning(false);
        props.onSubmitForRefinement();
    }

    const onPreviousItemClicked = useCallback(() => {
        if (hasPreviousItem) {
            setCurrentItemIndex((index) => index - 1);
        }
    }, [hasPreviousItem])

    const onNextItemClicked = useCallback(() => {
        if (hasNextItem) {
            setCurrentItemIndex((index) => index + 1);
        }
    }, [hasNextItem])

    const onItemUnavailable = (itemId: string) => {
        if (!props.sampleItemCollection?.items) {
            return;
        }
        const unavailableIndex = props.sampleItemCollection.items.findIndex((item) => item.itemId === itemId);
        if (unavailableIndex === -1) {
            return;
        }
        props.sampleItemCollection.items.splice(unavailableIndex, 1);
        // Items after the removed one shift down a slot, so leaving the index
        // unchanged when the removed item was current already advances us to
        // what was the next item; only indexes before it need to shift back.
        setCurrentItemIndex((index) => unavailableIndex < index ? index - 1 : index);
        forceReload();
    }

    const onCurrentTweetLoaded = () => {
        currentItemRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    const onTweetHtmlResolved = (itemId: string, tweetHtml: string) => {
        if (!props.sampleItemCollection?.items) {
            return;
        }
        for (let i = 0; i < props.sampleItemCollection.items.length; i++) {
            if (props.sampleItemCollection.items[i].itemId !== itemId)
                continue;
            props.sampleItemCollection.items[i].tweetHtml = tweetHtml;
            break;
        }
    }

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!currentItem || isSamplingComplete) {
                return;
            }
            if (event.ctrlKey || event.metaKey || event.altKey) {
                return;
            }
            const target = event.target as HTMLElement | null;
            const tagName = target?.tagName;
            if (tagName === "INPUT" || tagName === "TEXTAREA" || target?.isContentEditable) {
                return;
            }

            const key = event.key.toLowerCase();
            if (key === "1" || key === "y" || key === "+") {
                event.preventDefault();
                onMarkRelevant();
            } else if (key === "3" || key === "n" || key === "-") {
                event.preventDefault();
                onMarkNotRelevant();
            } else if (key === "arrowleft" && hasPreviousItem) {
                event.preventDefault();
                onPreviousItemClicked();
            } else if (key === "arrowright" && hasNextItem) {
                event.preventDefault();
                onNextItemClicked();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [currentItem, isSamplingComplete, hasPreviousItem, hasNextItem, onMarkRelevant, onMarkNotRelevant, onPreviousItemClicked, onNextItemClicked]);

    if (!props.sampleItemCollection?.items) {
        return (<></>);
    }

    return (
        <Box sx={{ border: "1px solid #888888", borderRadius: "10px", padding: "10px", maxWidth: "640px", marginRight: "auto", marginLeft: "auto", backgroundColor: "#eeeeee", position: "relative" }}>
            {/* Notes sit to the right of the whole workflow container, outside its width so the coding area
                keeps its full size. They are top- and bottom-aligned with the container; the note list scrolls. */}
            {currentItem && (
                <Box sx={{ position: "absolute", top: 0, bottom: 0, left: "calc(100% + 16px)", width: 300, minHeight: 150 }}>
                    <XSifterCodableItemNotes
                        key={currentItem.itemId}
                        itemId={currentItem.itemId}
                        codingRoundId={currentItem.codingRoundId}
                        codedSampleId={currentItem.codedSampleId}
                        savedQueryId={props.savedQueryId}
                        currentUser={props.currentUser}
                        onForceHeaderRemount={props.onForceHeaderRemount ?? (() => {})}
                    />
                </Box>
            )}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <Button variant="outlined" size="small" onClick={props.onCancelWorkflow}>Cancel</Button>
                {currentItem && (
                    <Box sx={{ display: "flex", gap: "8px" }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ArrowBackIcon />}
                            onClick={onPreviousItemClicked}
                            disabled={!hasPreviousItem}
                            sx={{ textTransform: "none", borderRadius: "20px", paddingX: "16px" }}
                        >
                            Previous
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            endIcon={<ArrowForwardIcon />}
                            onClick={onNextItemClicked}
                            disabled={!hasNextItem}
                            sx={{ textTransform: "none", borderRadius: "20px", paddingX: "16px" }}
                        >
                            Next
                        </Button>
                    </Box>
                )}
                <Button variant="contained" size="small" onClick={onRefineQueryClicked}>Refine Query</Button>
            </Box>
            <CodingProgressIndicator roundNumber={props.roundNumber} currentItemsCoded={itemsScored} totalItems={totalSampleItems} />

            <Typography variant="body2" gutterBottom sx={{ color: "text.secondary" }}>
                Score each sampled item as relevant or not relevant. Your responses will be used to refine the query.
            </Typography>

            <Divider />

            {isSamplingComplete && (
                <Box sx={{ position: "relative", overflow: "hidden", padding: "24px" }}>
                    <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
                        {confettiPieces.map((piece) => (
                            <Box
                                key={piece.id}
                                sx={{
                                    position: "absolute",
                                    top: "-10px",
                                    left: `${piece.left}%`,
                                    width: `${piece.size}px`,
                                    height: `${piece.round ? piece.size : piece.size * 0.6}px`,
                                    backgroundColor: piece.color,
                                    borderRadius: piece.round ? "50%" : "1px",
                                    animation: `${confettiFall} ${piece.duration}s ease-in ${piece.delay}s forwards`,
                                }}
                            />
                        ))}
                    </Box>
                    <Box sx={{ position: "relative", textAlign: "center" }}>
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                            <Typography variant="h6" component="span">{codingCompleteMessage.emoji}</Typography>
                            <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600 }}>{codingCompleteMessage.text}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: "text.secondary", marginTop: "8px" }}>
                            Use the Previous and Next buttons to review your coded items and change your answer if needed.
                        </Typography>
                    </Box>
                </Box>
            )}

            {currentItem && (
                <>
                    {isSamplingComplete && (
                        <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                            Current item:
                        </Typography>
                    )}
                    <XSifterCodableItem
                        ref={currentItemRef}
                        key={currentItem.itemId}
                        itemId={currentItem.itemId}
                        itemUsername={currentItem.itemUsername}
                        currentLabel={currentItem.label}
                        codingRoundId={props.codingRoundId}
                        tweetText={currentItem.tweetText}
                        tweetDisplayName={currentItem.tweetDisplayName}
                        tweetUsername={currentItem.tweetUsername}
                        cachedTweetHtml={currentItem.tweetHtml}
                        onMarkRelevant={onMarkRelevant}
                        onMarkNotRelevant={onMarkNotRelevant}
                        onRecodeItem={onRecodeCurrentItem}
                        onItemUnavailable={onItemUnavailable}
                        onTweetLoaded={onCurrentTweetLoaded}
                        onTweetHtmlResolved={onTweetHtmlResolved}
                    />
                </>
            )}

            <Dialog open={showIncompleteCodingWarning} onClose={onCancelIncompleteCodingWarning}>
                <DialogTitle>Not all items have been coded</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You have coded {itemsScored} of {totalSampleItems} sampled items.
                        Refining the query now may lead to inaccurate results or an inaccurate query refinement.
                        Do you want to proceed anyway?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCancelIncompleteCodingWarning}>Cancel</Button>
                    <Button onClick={onConfirmIncompleteCodingWarning} variant="contained">Refine Anyway</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default XSifterRevisionWorkflow;
